import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { APP_ID, YEAR_VALIDATION, isValidYear } from '../config/constants';

/**
 * Get Firestore instance
 */
const getDb = () => {
  return getFirestore();
};

/**
 * Get years collection path
 */
const getYearsPath = (userId) => {
  return `artifacts/${APP_ID}/users/${userId}/years`;
};

/**
 * Default years configuration
 */
const DEFAULT_YEARS = YEAR_VALIDATION.DEFAULT_YEARS.map(year => ({
  year,
  isDefault: true,
  status: 'active',
  hasData: false,
  notes: year === 2025 ? 'Năm tài chính mặc định' : 'Năm tài chính kế hoạch'
}));

/**
 * Initialize default years for new user
 * @param {string} userId 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const initializeDefaultYears = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const yearsPath = getYearsPath(userId);

    for (const yearData of DEFAULT_YEARS) {
      const yearRef = doc(getDb(), yearsPath, yearData.year.toString());
      await setDoc(yearRef, {
        ...yearData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error initializing default years:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all available years for user
 * @param {string} userId 
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const getAvailableYears = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const yearsPath = getYearsPath(userId);
    const yearsCollection = collection(getDb(), yearsPath);
    const snapshot = await getDocs(yearsCollection);

    const years = [];
    snapshot.forEach(doc => {
      years.push({ 
        id: doc.id,
        year: parseInt(doc.id),
        ...doc.data() 
      });
    });

    // Sort by year descending
    years.sort((a, b) => b.year - a.year);

    // If no years, initialize defaults
    if (years.length === 0) {
      await initializeDefaultYears(userId);
      return { 
        success: true, 
        data: DEFAULT_YEARS.map(y => ({ ...y, id: y.year.toString() })).sort((a, b) => b.year - a.year)
      };
    }

    return { success: true, data: years };
  } catch (error) {
    console.error('Error getting available years:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a new year
 * @param {string} userId 
 * @param {number} year 
 * @param {Object} options - { notes?: string }
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addYear = async (userId, year, options = {}) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!year) throw new Error('Year is required');

    // Validate year
    if (!isValidYear(year)) {
      throw new Error(`Năm phải trong khoảng ${YEAR_VALIDATION.MIN_YEAR} - ${new Date().getFullYear() + YEAR_VALIDATION.MAX_YEAR_OFFSET}`);
    }

    // Check if year already exists
    const yearsPath = getYearsPath(userId);
    const yearRef = doc(getDb(), yearsPath, year.toString());
    const yearSnap = await getDoc(yearRef);

    if (yearSnap.exists()) {
      throw new Error('Năm này đã tồn tại');
    }

    // Create new year
    const newYear = {
      year,
      isDefault: false,
      status: 'active',
      hasData: false,
      notes: options.notes || `Năm tài chính ${year}`,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(yearRef, newYear);

    return { success: true };
  } catch (error) {
    console.error('Error adding year:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update year status
 * @param {string} userId 
 * @param {number} year 
 * @param {string} status - 'active' | 'archived'
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateYearStatus = async (userId, year, status) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!year) throw new Error('Year is required');
    if (!['active', 'archived'].includes(status)) {
      throw new Error('Status must be active or archived');
    }

    const yearsPath = getYearsPath(userId);
    const yearRef = doc(getDb(), yearsPath, year.toString());
    const yearSnap = await getDoc(yearRef);

    if (!yearSnap.exists()) {
      throw new Error('Năm không tồn tại');
    }

    await setDoc(yearRef, {
      ...yearSnap.data(),
      status,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating year status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if year has data
 * @param {string} userId 
 * @param {number} year 
 * @returns {Promise<{hasData: boolean, details: Object}>}
 */
export const checkYearHasData = async (userId, year) => {
  try {
    if (!userId || !year) {
      return { hasData: false, details: {} };
    }

    const db = getDb();
    const userPath = `artifacts/${APP_ID}/users/${userId}`;

    // Check in multiple collections
    const [incomesSnap, expensesSnap, plannedIncomesSnap, plannedExpensesSnap] = await Promise.all([
      getDocs(query(
        collection(db, `${userPath}/incomes`),
        where('year', '==', year)
      )),
      getDocs(query(
        collection(db, `${userPath}/expenses`),
        where('year', '==', year)
      )),
      getDocs(query(
        collection(db, `${userPath}/plannedIncomes`),
        where('year', '==', year)
      )),
      getDocs(query(
        collection(db, `${userPath}/plannedExpenses`),
        where('year', '==', year)
      ))
    ]);

    const details = {
      incomes: incomesSnap.size,
      expenses: expensesSnap.size,
      plannedIncomes: plannedIncomesSnap.size,
      plannedExpenses: plannedExpensesSnap.size
    };

    const hasData = Object.values(details).some(count => count > 0);

    return { hasData, details };
  } catch (error) {
    console.error('Error checking year data:', error);
    return { hasData: false, details: {}, error: error.message };
  }
};

/**
 * Check if year can be deleted
 * @param {string} userId 
 * @param {number} year 
 * @returns {Promise<{canDelete: boolean, reason?: string}>}
 */
export const canDeleteYear = async (userId, year) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!year) throw new Error('Year is required');

    // Get year data
    const yearsPath = getYearsPath(userId);
    const yearRef = doc(getDb(), yearsPath, year.toString());
    const yearSnap = await getDoc(yearRef);

    if (!yearSnap.exists()) {
      return { canDelete: false, reason: 'Năm không tồn tại' };
    }

    const yearData = yearSnap.data();

    // Cannot delete default years
    if (yearData.isDefault) {
      return { canDelete: false, reason: 'Không thể xóa năm mặc định' };
    }

    // Check if has data
    const { hasData, details } = await checkYearHasData(userId, year);

    if (hasData) {
      const dataTypes = [];
      if (details.incomes > 0) dataTypes.push(`${details.incomes} thu nhập`);
      if (details.expenses > 0) dataTypes.push(`${details.expenses} chi tiêu`);
      if (details.plannedIncomes > 0) dataTypes.push(`${details.plannedIncomes} dự thu`);
      if (details.plannedExpenses > 0) dataTypes.push(`${details.plannedExpenses} dự chi`);
      
      return { 
        canDelete: false, 
        reason: `Năm ${year} có dữ liệu: ${dataTypes.join(', ')}. Không thể xóa.`
      };
    }

    return { canDelete: true };
  } catch (error) {
    console.error('Error checking can delete year:', error);
    return { canDelete: false, reason: error.message };
  }
};

/**
 * Delete a year
 * @param {string} userId 
 * @param {number} year 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteYear = async (userId, year) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!year) throw new Error('Year is required');

    // Check if can delete
    const { canDelete, reason } = await canDeleteYear(userId, year);

    if (!canDelete) {
      throw new Error(reason);
    }

    // Delete year
    const yearsPath = getYearsPath(userId);
    const yearRef = doc(getDb(), yearsPath, year.toString());
    await deleteDoc(yearRef);

    return { success: true };
  } catch (error) {
    console.error('Error deleting year:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update year hasData flag
 * This should be called when adding/deleting incomes/expenses
 * @param {string} userId 
 * @param {number} year 
 * @returns {Promise<void>}
 */
export const updateYearDataFlag = async (userId, year) => {
  try {
    if (!userId || !year) return;

    const { hasData } = await checkYearHasData(userId, year);

    const yearsPath = getYearsPath(userId);
    const yearRef = doc(getDb(), yearsPath, year.toString());
    const yearSnap = await getDoc(yearRef);

    if (yearSnap.exists()) {
      await setDoc(yearRef, {
        ...yearSnap.data(),
        hasData,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Error updating year data flag:', error);
  }
};

/**
 * Get year statistics (incomes, expenses, planned items)
 * @param {string} userId 
 * @param {number} year 
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getYearStatistics = async (userId, year) => {
  try {
    if (!userId || !year) {
      return { success: false, error: 'User ID and year are required' };
    }

    const { hasData, details } = await checkYearHasData(userId, year);

    return {
      success: true,
      data: {
        year,
        hasData,
        incomes: details.incomes || 0,
        expenses: details.expenses || 0,
        plannedIncomes: details.plannedIncomes || 0,
        plannedExpenses: details.plannedExpenses || 0,
        totalTransactions: (details.incomes || 0) + (details.expenses || 0),
        totalPlanned: (details.plannedIncomes || 0) + (details.plannedExpenses || 0)
      }
    };
  } catch (error) {
    console.error('Error getting year statistics:', error);
    return { success: false, error: error.message };
  }
};
